/**
 * RepairBookingTab — driver self-service repair calendar booking.
 *
 * - Shows a calendar for the next 30 days
 * - Each date shows availability (0/3 to 3/3 booked)
 * - Driver picks a date, then a time slot (09:00 / 14:00 / 17:00)
 * - Fills in issue description, priority, optional damage photo
 * - Bookings appear in the mechanic's Repairs tab as status="scheduled"
 * - Driver also sees "My Upcoming Repairs" list below the calendar
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, Camera, X, Loader2, AlertCircle, CheckCircle2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = ["09:00", "14:00", "17:00"];

// Get YYYY-MM-DD for a Date object in Singapore timezone.
const toSgtIsoDate = (d) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(d);

// Format YYYY-MM-DD → "Mon 5 Aug"
const formatDayLabel = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

// Compress an image file to base64 under ~200KB using canvas.
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const MAX_DIM = 1200;
        if (width > MAX_DIM || height > MAX_DIM) {
          const scale = MAX_DIM / Math.max(width, height);
          width *= scale;
          height *= scale;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        let q = 0.8;
        let dataUrl = canvas.toDataURL("image/jpeg", q);
        while (dataUrl.length > 200 * 1024 * 1.37 && q > 0.2) {
          q -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RepairBookingTab({ user }) {
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [issue, setIssue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [damagePhoto, setDamagePhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [myBookings, setMyBookings] = useState({ upcoming: [], past: [] });
  const [weekStart, setWeekStart] = useState(() => new Date());
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null); // when set, form is in edit mode
  const [photoTouched, setPhotoTouched] = useState(false); // true if user changed photo during edit

  // Build the visible date range (14 days from weekStart)
  const dateRange = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return toSgtIsoDate(d);
  });

  const fetchAvailability = useCallback(async () => {
    setLoadingAvailability(true);
    try {
      const results = await Promise.all(
        dateRange.map((date) =>
          axios
            .get(`${API}/repair-bookings/availability?date=${date}`)
            .then((r) => [date, r.data])
            .catch(() => [date, null])
        )
      );
      const map = {};
      for (const [date, data] of results) if (data) map[date] = data;
      setAvailabilityByDate(map);
    } finally {
      setLoadingAvailability(false);
    }
  }, [weekStart]);

  const fetchMyBookings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/driver/repair-bookings`);
      setMyBookings(r.data || { upcoming: [], past: [] });
    } catch (e) {
      // silently
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setDamagePhoto(compressed);
      setPhotoTouched(true);
      toast.success("Photo added");
    } catch {
      toast.error("Failed to process photo");
    }
  };

  const resetForm = () => {
    setSelectedDate(null);
    setSelectedSlot(null);
    setIssue("");
    setPriority("medium");
    setDamagePhoto(null);
    setEditingBookingId(null);
    setPhotoTouched(false);
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm("Delete this booking? The time slot will open up for other drivers.")) return;
    try {
      await axios.delete(`${API}/driver/repair-bookings/${bookingId}`);
      toast.success("Booking deleted");
      if (editingBookingId === bookingId) resetForm();
      fetchAvailability();
      fetchMyBookings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete booking");
    }
  };

  const startEdit = (b) => {
    setEditingBookingId(b.id);
    setSelectedDate(b.assigned_date);
    setSelectedSlot(b.time_slot);
    setIssue(b.issue_description || "");
    setPriority(b.priority || "medium");
    setDamagePhoto(null); // photo not fetched in list; user can re-upload if needed
    setPhotoTouched(false);
    // Ensure the calendar strip includes this booking's date
    const bookingDate = new Date(b.assigned_date + "T00:00:00");
    setWeekStart(bookingDate);
    // Scroll form into view
    setTimeout(() => {
      document.querySelector('[data-testid="booking-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const submitBooking = async () => {
    if (!selectedDate) return toast.error("Please pick a date");
    if (!selectedSlot) return toast.error("Please pick a time slot");
    if (!issue.trim()) return toast.error("Please describe the issue");
    setSubmitting(true);
    try {
      if (editingBookingId) {
        // Edit mode: only send fields that could have changed; only include photo if touched
        const payload = {
          date: selectedDate,
          time_slot: selectedSlot,
          issue_description: issue.trim(),
          priority,
        };
        if (photoTouched) payload.damage_photo = damagePhoto || "";
        await axios.patch(`${API}/driver/repair-bookings/${editingBookingId}`, payload);
        toast.success(`Booking updated: ${formatDayLabel(selectedDate)} at ${selectedSlot}`);
      } else {
        await axios.post(`${API}/repair-bookings`, {
          date: selectedDate,
          time_slot: selectedSlot,
          issue_description: issue.trim(),
          priority,
          damage_photo: damagePhoto || null,
        });
        toast.success(`Repair booked for ${formatDayLabel(selectedDate)} at ${selectedSlot}`);
      }
      resetForm();
      fetchAvailability();
      fetchMyBookings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save booking");
    } finally {
      setSubmitting(false);
    }
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const availabilityFor = (iso) => availabilityByDate[iso];
  const selectedAvail = selectedDate ? availabilityFor(selectedDate) : null;
  const today = toSgtIsoDate(new Date());

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border-2 border-black/10 p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Wrench className="w-6 h-6 text-primary" strokeWidth={2} />
          <h2 className="font-heading font-bold text-xl uppercase">BOOK REPAIR</h2>
        </div>
        <p className="text-sm text-black/60">
          Pick a date and time slot for your vehicle repair. Maximum 3 bookings per day (one per slot). Your vehicle:{" "}
          <span className="font-mono font-bold">{user?.truck_number || "not set"}</span>
        </p>
      </div>

      {/* Calendar strip */}
      <div className="bg-white border-2 border-black/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => shiftWeek(-1)}
            disabled={dateRange[0] <= today}
            className="p-2 hover:bg-black/5 disabled:opacity-30"
            data-testid="cal-prev-week"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <div className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
            <CalendarIcon className="w-4 h-4" strokeWidth={2} />
            {formatDayLabel(dateRange[0])} — {formatDayLabel(dateRange[dateRange.length - 1])}
          </div>
          <button onClick={() => shiftWeek(1)} className="p-2 hover:bg-black/5" data-testid="cal-next-week">
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {dateRange.map((iso) => {
            const avail = availabilityFor(iso);
            const isPast = iso < today;
            const isSelected = iso === selectedDate;
            const taken = avail?.total_taken ?? 0;
            const max = avail?.max_per_day ?? 3;
            // When editing, the booking's own date/slot should not count as "full"
            const isFull = taken >= max && !(editingBookingId && iso === selectedDate);
            const disabled = isPast || isFull || loadingAvailability;
            return (
              <button
                key={iso}
                onClick={() => !disabled && setSelectedDate(iso)}
                disabled={disabled}
                className={`p-2 border-2 transition-all text-left ${
                  isSelected
                    ? "border-primary bg-primary/10"
                    : disabled
                    ? "border-black/5 bg-black/5 opacity-40 cursor-not-allowed"
                    : "border-black/10 hover:border-primary/50"
                }`}
                data-testid={`cal-day-${iso}`}
              >
                <div className="text-xs font-heading font-bold uppercase">
                  {formatDayLabel(iso)}
                </div>
                <div className="text-[10px] text-black/60 mt-1">
                  {loadingAvailability ? "…" : isFull ? "FULL" : `${taken}/${max} booked`}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Booking form (visible when date selected) */}
      {selectedDate && selectedAvail && (
        <div className="bg-white border-2 border-black/10 p-4 sm:p-6 space-y-4" data-testid="booking-form">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold uppercase text-sm">
              {editingBookingId ? "Edit booking on " : "Book on "}{formatDayLabel(selectedDate)}
            </h3>
            <button onClick={resetForm} className="text-black/40 hover:text-black" data-testid="cancel-booking-form">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>

          {/* Time slot picker */}
          <div>
            <label className="block text-xs font-heading font-bold uppercase text-black/60 mb-2">
              Time Slot *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {selectedAvail.slots.map((s) => {
                // When editing, treat the current booking's own slot as free (so the driver can keep the same slot)
                const currentBooking = editingBookingId
                  ? myBookings.upcoming.find((b) => b.id === editingBookingId)
                  : null;
                const isOwnCurrent =
                  currentBooking &&
                  currentBooking.assigned_date === selectedDate &&
                  currentBooking.time_slot === s.slot;
                const taken = s.taken && !isOwnCurrent;
                return (
                  <button
                    key={s.slot}
                    onClick={() => !taken && setSelectedSlot(s.slot)}
                    disabled={taken}
                    className={`p-3 border-2 flex flex-col items-center gap-1 transition-all ${
                      selectedSlot === s.slot
                        ? "border-primary bg-primary/10"
                        : taken
                        ? "border-black/5 bg-black/5 opacity-50 cursor-not-allowed"
                        : "border-black/10 hover:border-primary/50"
                    }`}
                    data-testid={`slot-${s.slot}`}
                  >
                    <Clock className="w-4 h-4" strokeWidth={2} />
                    <span className="font-heading font-bold text-sm">{s.slot}</span>
                    <span className="text-[10px] text-black/60">
                      {taken ? "TAKEN" : isOwnCurrent ? "CURRENT" : "AVAILABLE"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Issue description */}
          <div>
            <label className="block text-xs font-heading font-bold uppercase text-black/60 mb-2">
              Issue Description *
            </label>
            <textarea
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              placeholder="e.g. Brakes squeaking, engine warning light..."
              className="w-full min-h-[80px] p-2 border-2 border-black/10 focus:border-primary focus:outline-none text-sm"
              maxLength={500}
              data-testid="booking-issue"
            />
            <div className="text-xs text-black/40 mt-1">{issue.length}/500</div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-xs font-heading font-bold uppercase text-black/60 mb-2">
              Priority
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["low", "medium", "high"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className={`p-2 border-2 uppercase font-heading font-bold text-xs ${
                    priority === p
                      ? p === "high"
                        ? "border-red-500 bg-red-500/10 text-red-600"
                        : p === "medium"
                        ? "border-yellow-500 bg-yellow-500/10 text-yellow-700"
                        : "border-green-500 bg-green-500/10 text-green-700"
                      : "border-black/10 hover:border-black/30"
                  }`}
                  data-testid={`priority-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Damage photo (optional) */}
          <div>
            <label className="block text-xs font-heading font-bold uppercase text-black/60 mb-2">
              Damage Photo <span className="text-black/40 normal-case font-normal">(optional)</span>
            </label>
            {damagePhoto ? (
              <div className="relative inline-block">
                <img src={damagePhoto} alt="Damage" className="max-h-32 border-2 border-black/10" />
                <button
                  onClick={() => { setDamagePhoto(null); setPhotoTouched(true); }}
                  className="absolute top-1 right-1 bg-black/70 text-white p-1"
                  data-testid="remove-damage-photo"
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-black/20 bg-black/[0.02] cursor-pointer hover:border-primary text-sm text-black/60">
                <Camera className="w-4 h-4" strokeWidth={2} />
                <span>{editingBookingId ? "Replace photo (optional)" : "Upload photo (optional)"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  data-testid="booking-photo-input"
                />
              </label>
            )}
          </div>

          <button
            onClick={submitBooking}
            disabled={submitting || !selectedSlot || !issue.trim()}
            className="w-full h-12 bg-primary text-white font-heading font-bold uppercase disabled:bg-black/20 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-booking"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                {editingBookingId ? "SAVING…" : "BOOKING…"}
              </>
            ) : (
              <>
                <Wrench className="w-4 h-4" strokeWidth={2} /> {editingBookingId ? "SAVE CHANGES" : "CONFIRM BOOKING"}
              </>
            )}
          </button>
        </div>
      )}

      {/* My upcoming bookings */}
      <div className="bg-white border-2 border-black/10 p-4">
        <h3 className="font-heading font-bold uppercase text-sm mb-3">
          My Upcoming Repairs ({myBookings.upcoming.length})
        </h3>
        {myBookings.upcoming.length === 0 ? (
          <div className="text-sm text-black/50 italic">No upcoming repairs booked.</div>
        ) : (
          <div className="space-y-2">
            {myBookings.upcoming.map((b) => {
              const canEdit = b.status === "scheduled";
              const isEditingThis = editingBookingId === b.id;
              return (
                <div key={b.id} className={`border p-3 flex items-start justify-between gap-3 ${isEditingThis ? "border-primary bg-primary/5" : "border-black/10"}`} data-testid={`upcoming-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <CalendarIcon className="w-3.5 h-3.5 text-black/40" strokeWidth={2} />
                      <span className="text-sm font-bold">{formatDayLabel(b.assigned_date)} · {b.time_slot}</span>
                      <span
                        className={`px-2 py-0.5 text-[10px] uppercase font-bold ${
                          b.status === "scheduled"
                            ? "bg-blue-500/10 text-blue-700"
                            : b.status === "in_progress"
                            ? "bg-yellow-500/10 text-yellow-700"
                            : "bg-green-500/10 text-green-700"
                        }`}
                      >
                        {b.status}
                      </span>
                      {b.mechanic_name && (
                        <span className="px-2 py-0.5 text-[10px] uppercase font-bold bg-black/5 text-black/70">
                          Mech: {b.mechanic_name}
                        </span>
                      )}
                      {b.priority && (
                        <span
                          className={`px-2 py-0.5 text-[10px] uppercase font-bold ${
                            b.priority === "high"
                              ? "text-red-600"
                              : b.priority === "medium"
                              ? "text-yellow-700"
                              : "text-green-700"
                          }`}
                        >
                          {b.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-black/70">{b.issue_description || b.title}</p>
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1.5">
                      <button
                        onClick={() => (isEditingThis ? resetForm() : startEdit(b))}
                        className="px-3 py-1.5 border-2 border-primary text-primary hover:bg-primary hover:text-white font-heading font-bold uppercase text-xs whitespace-nowrap"
                        data-testid={`edit-booking-${b.id}`}
                      >
                        {isEditingThis ? "Cancel" : "Edit"}
                      </button>
                      <button
                        onClick={() => handleDelete(b.id)}
                        className="px-3 py-1.5 border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-heading font-bold uppercase text-xs whitespace-nowrap flex items-center justify-center gap-1"
                        data-testid={`delete-booking-${b.id}`}
                        title="Delete booking"
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={2} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
